import { cos, hypot, sin } from '@gardener/core';
import { ticks } from './config';
import type { KnockDef } from './weapons';

/**
 * A battle royale's cars (ADR-0214): parked about the map as a round starts, got into with SPACE, driven with W (gas), S
 * (brake, then reverse) and A / D (steer), out again with SPACE. Their seats: the driver's sight is the car's, wide and
 * long, and it holds its fire; everyone else in it aims and shoots as on foot. A shot or a blast at anyone in a car hurts
 * the car alone; a car out of health blows up, throwing its riders out into its blast. They never run out of petrol.
 */
export const CarKind = {
  /** A farm pickup: the driver in its cab (hidden), a gunner standing in its bed. */
  Pickup: 0,
  /** An open jeep: the driver at the wheel, a gunner behind on its roll bar. */
  Jeep: 1,
  /** A four-seat open G-wagon: the driver, a mate beside them, two behind. */
  Wagon: 2,
  /** The G-wagon in sand and olive: a bull bar, a light bar on its roll bar, a snorkel. */
  Safari: 3,
  /** The G-wagon in forest green: wood-panelled sides, chrome bumpers, cream seats. */
  Woodie: 4,
  /** The G-wagon in tangerine: cream racing stripes, a bonnet scoop, spot lamps, a number on its doors. */
  Rally: 5,
  /** A scooter: two on its saddle, its driver steering with one hand and shooting with the other; a few shots wreck it. */
  Scooter: 6,
} as const;
export type CarKind = (typeof CarKind)[keyof typeof CarKind];

/**
 * A seat in a car, in its own px (x forward, y to its right, from its middle): where its rider's feet are and how high
 * (`z` px up: what of them its body hides is the client's), the driver's first. `shown`: its rider is seen (a closed cab's
 * driver is not).
 */
export interface SeatDef {
  x: number;
  y: number;
  z: number;
  shown: boolean;
}

/**
 * How a car drives (px, px/s, rad/s): `max` its top speed, `accel` its pull, `steer` how fast it turns at speed;
 * `grip` how fast its tyres kill a slide (1/s), `slideGrip` as little as they hold once it slides (a drift).
 */
export interface Handling {
  max: number;
  accel: number;
  steer: number;
  grip: number;
  slideGrip: number;
}

export interface CarDef {
  key: 'pickup' | 'jeep' | 'wagon' | 'safari' | 'woodie' | 'rally' | 'scooter';
  name: string;
  /** Half its length and half its width (px): the box shots and blasts hit, and the box that stops gardeners on foot. */
  half: { l: number; w: number };
  /** The circles along its length (their x, px from its middle; `r` each) that meet walls and what stands on the map. */
  wheels: { x: readonly number[]; r: number };
  hp: number;
  seats: readonly SeatDef[];
  handling: Handling;
  /**
   * Its driver keeps a hand free (a scooter, the owner's 2026-11-03): they aim, see and shoot as on foot while they steer,
   * never along the car (`handsOnWheel`).
   */
  shoots?: true;
  /** Health a wall costs it a px/s past `CAR.crash.min` (`CAR.crash.damage` if not said). */
  crash?: number;
}

const WAGON: CarDef = {
  key: 'wagon',
  name: 'G-Wagon',
  half: { l: 21, w: 10 },
  wheels: { x: [-13.5, 0, 13.5], r: 8.5 },
  hp: 450,
  seats: [
    { x: 2, y: -4.5, z: 2.5, shown: true },
    { x: 2, y: 4.5, z: 2.5, shown: true },
    { x: -9.5, y: -4.5, z: 4, shown: true },
    { x: -9.5, y: 4.5, z: 4, shown: true },
  ],
  handling: { max: 160, accel: 210, steer: 2.5, grip: 8, slideGrip: 2.8 },
};

/** Indexed by `CarKind`. The Safari, the Woodie and the Rally are the G-wagon in another trim: its seats, box and drive. */
export const CARS: readonly CarDef[] = [
  {
    key: 'pickup',
    name: 'Pickup',
    half: { l: 18.5, w: 8.5 },
    wheels: { x: [-12, 0, 12], r: 7 },
    hp: 300,
    seats: [
      { x: 1.5, y: -3.5, z: 2, shown: false },
      { x: -10.5, y: 0, z: 7.75, shown: true },
    ],
    handling: { max: 170, accel: 230, steer: 2.8, grip: 7.5, slideGrip: 2.6 },
  },
  {
    key: 'jeep',
    name: 'Jeep',
    half: { l: 18.5, w: 8.5 },
    wheels: { x: [-12, 0, 12], r: 7 },
    hp: 260,
    seats: [
      { x: -1, y: -3.5, z: 2, shown: true },
      { x: -11, y: 0, z: 5, shown: true },
    ],
    handling: { max: 180, accel: 260, steer: 3, grip: 7, slideGrip: 2.4 },
  },
  WAGON,
  { ...WAGON, key: 'safari', name: 'Safari' },
  { ...WAGON, key: 'woodie', name: 'Woodie' },
  { ...WAGON, key: 'rally', name: 'Rally' },
  {
    key: 'scooter',
    name: 'Scooter',
    half: { l: 11, w: 4.5 },
    wheels: { x: [-6.5, 6.5], r: 4.5 },
    hp: 40,
    seats: [
      { x: 0.5, y: 0, z: 3, shown: true },
      { x: -5.5, y: 0, z: 3.5, shown: true },
    ],
    handling: { max: 185, accel: 300, steer: 3.4, grip: 9, slideGrip: 3.4 },
    shoots: true,
    crash: 0.05,
  },
];

/** The most seats a car has (the wire's `seats`). */
export const MAX_SEATS = 4;

export const carDef = (kind: number): CarDef => CARS[kind] ?? (CARS[0] as CarDef);

/**
 * The rider in seat `seat` of a car of `kind` has its hands on the wheel (ADR-0214): it drives, sees along the car (wide
 * and long, `DRIVE_EYE`) and holds its fire. A scooter's driver (`shoots`) does none of the last two: it aims, sees and
 * shoots as on foot.
 */
export const handsOnWheel = (kind: number, seat: number): boolean => seat === 0 && !carDef(kind).shoots;

/**
 * Every car's (ADR-0214):
 * - `reach`: SPACE within this many px of a car's box gets you in (the driver's seat first, else the next free one).
 * - Driving: `brake` px/s² to a stop under S, then `reverse` px/s² back to `back` px/s; `coast` px/s² lost with no gas;
 *   steering bites fully from `bite` px/s and fades with speed by `fade` (a turn's rate over 1 + speed / fade). A slide
 *   (a drift) starts once the tyres slip sideways at `slideIn` px/s, and holds (`slideGrip`) until they slip under
 *   `slideOut`; a car sliding turns `slideTurn` times as fast and scrubs `scrub` px/s² off its speed.
 * - With nobody at its wheel it rolls to a stop at `park` px/s² (its hand brake).
 * - A wall met at more than `crash.min` px/s across it costs `crash.damage` health a px/s past that (a car's own `crash`
 *   if it says: a scooter's few points of health go to shots, not to every kerb).
 * - A thing of the map that breaks (ADR-0188: a crate, a barrel, a woodpile) never stops it (the owner, 2026-10-27): it
 *   smashes through, losing `smash.slow` of its speed a point of the thing's health, `smash.most` at most (a pot barely
 *   checks it, a woodpile halves it). Another car still stops it.
 * - Driven into a foe on foot at more than `ram.min` px/s it deals up to `ram.damage` (at top speed) and throws them off
 *   with `ram.knock`, once every `ram.every` ticks a foe.
 * - Out of health it blows up (`blast`: its riders thrown out into it, `out` px off its side), thrown up by it (the
 *   owner, 2026-10-29: "it also lost 100% of its momentum"): it flies on the way it went and skids to a stop, `wreckSkid`
 *   px/s² off its speed, and lies a burnt wreck for `wreckTicks`, in the way.
 * - A battle royale's round on one of `royale.maps` starts with `royale.wagons` four-seaters (the owner, 2026-10-26,
 *   wanted only those, and more of them: forty, from fifteen cars; then fewer: twenty-four; 2026-10-27: in variants),
 *   each one of `royale.kinds` at random, spread evenly over the map proper's open ground (`room` px clear round each,
 *   `road` its paths' share of the spots it picks from), and among them `royale.scooters` scooters (the owner,
 *   2026-11-03: two seats, a driver who shoots, a few shots wreck it). A map's own bays (`MapDef.parked`, ADR-0232
 *   amended) are filled first and count among them. The pickup and the jeep stay for the dev knob `?car=` alone.
 * - Its `radio` (the owner, 2026-10-26: one you can't turn off, heard by everyone round it): it plays `songs` (by the
 *   client's `RADIO_TRACKS` id, each `seconds` long) one after another from the round's start, whoever listens: since
 *   2026-10-27 the one tech house loop the owner picked for a car's hard, steady beat, a song of five passes of it
 *   (76.8 s, a whole number of ticks, so the client's loop never drifts off the server's); it sounds while anyone sits
 *   in the car, and only its driver puts on the next song (Q). Its sound and its engine's are told every `every` ticks
 *   (and as a song changes) to every squad with a gardener within `reach` px of it (a little past the client's shot
 *   cues' range, ADR-0191 amended, which they are heard and cued over).
 */
export const CAR = {
  reach: 12,
  brake: 300,
  reverse: 70,
  back: 60,
  coast: 40,
  bite: 45,
  fade: 320,
  slideIn: 34,
  slideOut: 13,
  slideTurn: 1.25,
  scrub: 18,
  park: 140,
  crash: { min: 70, damage: 0.3 },
  smash: { slow: 0.0025, most: 0.5 },
  ram: {
    min: 55,
    damage: 32,
    knock: { speed: 300, ticks: 8, decay: 0.8, slam: false } satisfies KnockDef,
    every: ticks(0.6),
  },
  blast: {
    radius: 72,
    damage: 70,
    knock: { speed: 330, ticks: 9, decay: 0.82, slam: false } satisfies KnockDef,
  },
  out: 6,
  wreckSkid: 240,
  wreckTicks: ticks(40),
  royale: {
    maps: ['village'] as readonly string[],
    wagons: 24,
    kinds: [CarKind.Wagon, CarKind.Safari, CarKind.Woodie, CarKind.Rally] as readonly CarKind[],
    scooters: 12,
    room: 6,
    road: 0.7,
  },
  radio: {
    songs: [{ id: 'ready-steady-go', seconds: 5 * 15.36 }],
    every: ticks(0.25),
    reach: 640,
  },
} as const;

/** A car radio's song `song` (an index into `CAR.radio.songs`, wrapped), and how long it plays (ticks). */
export const radioSong = (song: number) => {
  const { songs } = CAR.radio;
  const s = songs[((song % songs.length) + songs.length) % songs.length] as (typeof songs)[number];
  return { id: s.id, ticks: ticks(s.seconds) };
};

/** Where seat `s` of a car of `kind` at (x, y) heading `heading` is, on the ground (its rider's feet, `z` aside). */
export function seatAt(kind: number, s: number, x: number, y: number, heading: number): { x: number; y: number } {
  const seat = carDef(kind).seats[s] ?? { x: 0, y: 0 };
  const c = cos(heading);
  const n = sin(heading);
  return { x: x + seat.x * c - seat.y * n, y: y + seat.x * n + seat.y * c };
}

/**
 * How far (px) point (px, py) is outside the box of a car of `kind` at (x, y) heading `heading` (0 or less: inside), and
 * which way out of it is nearest (`out` set to the unit normal there). The box shots, blasts and feet meet.
 */
export function carEdge(
  kind: number,
  x: number,
  y: number,
  heading: number,
  px: number,
  py: number,
  out?: { x: number; y: number },
): number {
  const { l, w } = carDef(kind).half;
  const c = cos(heading);
  const n = sin(heading);
  const dx = px - x;
  const dy = py - y;
  // Into the car's own frame.
  const u = dx * c + dy * n;
  const v = -dx * n + dy * c;
  const ou = Math.abs(u) - l;
  const ov = Math.abs(v) - w;
  if (ou <= 0 && ov <= 0) {
    // Inside: out the nearer side.
    const [nu, nv] = ou > ov ? [Math.sign(u) || 1, 0] : [0, Math.sign(v) || 1];
    if (out) {
      out.x = nu * c - nv * n;
      out.y = nu * n + nv * c;
    }
    return Math.max(ou, ov);
  }
  const cu = Math.max(0, ou) * (Math.sign(u) || 1);
  const cv = Math.max(0, ov) * (Math.sign(v) || 1);
  const d = hypot(cu, cv);
  if (out) {
    out.x = (cu * c - cv * n) / d;
    out.y = (cu * n + cv * c) / d;
  }
  return d;
}

/**
 * Where segment (x0, y0) + t (dx, dy), t in 0 … 1, first meets the box of a car of `kind` at (x, y) heading `heading`
 * grown by `grow` px all round: its t, or -1 when it misses. The box a shot hits.
 */
export function segmentCar(
  kind: number,
  x: number,
  y: number,
  heading: number,
  x0: number,
  y0: number,
  dx: number,
  dy: number,
  grow: number,
): number {
  const { l, w } = carDef(kind).half;
  const c = cos(heading);
  const n = sin(heading);
  // The segment in the car's own frame, the box axis-aligned there: a slab test.
  const sx = (x0 - x) * c + (y0 - y) * n;
  const sy = -(x0 - x) * n + (y0 - y) * c;
  const vx = dx * c + dy * n;
  const vy = -dx * n + dy * c;
  let lo = 0;
  let hi = 1;
  for (const [s, v, half] of [
    [sx, vx, l + grow],
    [sy, vy, w + grow],
  ] as const) {
    if (Math.abs(v) < 1e-12) {
      if (Math.abs(s) > half) return -1;
      continue;
    }
    let t0 = (-half - s) / v;
    let t1 = (half - s) / v;
    if (t0 > t1) [t0, t1] = [t1, t0];
    lo = Math.max(lo, t0);
    hi = Math.min(hi, t1);
    if (lo > hi) return -1;
  }
  return lo;
}
